// @ts-nocheck

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { Cross2 as Cross } from '../../assets/svg/common';
import { Target } from '../../client';
import { useViewportContext } from '../../contexts/ViewportContext';
import { getAppDOMElement } from '../../DOMElements';
import useMatchBetFiltersHandlerContext from '../../hooks/useMatchBetFiltersHandlerContext';
import useMatchViewContext from '../../hooks/useMatchViewContext';
import { SMOOTH_SCROLL_TIMEOUT, smoothScrollToPosition } from '../../utils/smoothScroll';
import BScrollView from '../BScrollView';
import StickyScrollWrapper from '../StickyScrollWrapper';

const WRAP_SPACING = 20;

const Wrapper = styled(StickyScrollWrapper)`
  align-items: center;
  display: flex;
  justify-content: flex-start;
  margin: 0;

  ${({ $hasBackButton }) =>
    $hasBackButton
      ? css`
          margin-left: var(--FiltersWrapper-marginLeft, 0);
        `
      : undefined}
`;

const InnerWrap = styled.div`
  display: inline-block;
  position: relative;
  height: 100%;
`;

export const FilterWrapperListClassName = 'filter-wrapper-list';
const List = styled.div`
  position: relative;
  white-space: nowrap;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.1em 0;
`;

const BackButton = styled.div`
  background: white;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.65em;
  margin-right: 0.3em;
  transition: none 150ms ease-in-out;
  transition-property: transform, filter;

  & > svg {
    width: 0.7em;
    height: 0.7em;
  }

  &:active {
    transform: scale(0.95);
  }

  ${!Target.__MOBILE__
    ? css`
        cursor: pointer;

        &:hover {
          filter: brightness(110%) drop-shadow(0 0 0.1em white);
        }
      `
    : undefined}
`;

const ScrollViewWrapper = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
`;

const FiltersWrapper = ({
  className,
  onBackClick,
  noAutoScroll = false,
  children,
  filtersElementsRef,
  hasAutoStickyBehaviour,
}) => {
  const { navbarHeight } = useViewportContext();
  const [filterSelectedElement, setFilterSelectedElement] = useState(null);

  const scrollElement = Target.__MOBILE__ ? getAppDOMElement() : window;
  const initialWrapperPosition = useRef(0);
  const scrollTimer = useRef();

  const [wrapperSize, setWrapperSize] = useState({ width: 0, height: 0 });

  const { currentFilter } = useMatchBetFiltersHandlerContext();
  const { isWidgetSticky, widgetSize, stickyHeaderSize, promoSize, toolbarSize, initialHeaderSize } =
    useMatchViewContext();

  const fixedContentSize = useMemo(
    () => (isWidgetSticky ? widgetSize : stickyHeaderSize) ?? 0,
    [isWidgetSticky, stickyHeaderSize, widgetSize],
  );

  const spaceAboveFilters = useMemo(() => (widgetSize ?? 0) + (promoSize ?? 0), [widgetSize, promoSize]);

  const scrollToButtonFilters = useCallback(() => {
    if (scrollTimer.current) {
      clearTimeout(scrollTimer.current);
    }

    const magicalRatio = 1.5; // Completely arbitrary
    const wrapperPosition = Target.__MOBILE__
      ? (toolbarSize ?? 0) + (initialHeaderSize ?? 0) + spaceAboveFilters + wrapperSize.height / magicalRatio
      : 0;

    const scrollTopTo = wrapperPosition - fixedContentSize;
    const scrollPosition = scrollElement.scrollTop ?? scrollElement.scrollY;

    if (scrollPosition > scrollTopTo) {
      scrollTimer.current = setTimeout(() => {
        smoothScrollToPosition(scrollTopTo, scrollElement, SMOOTH_SCROLL_TIMEOUT);
      }, 2);
    }
  }, [scrollElement, fixedContentSize, spaceAboveFilters, initialHeaderSize, toolbarSize, wrapperSize]);

  const scrollToButtonFiltersRef = useRef();

  useEffect(() => {
    scrollToButtonFiltersRef.current = scrollToButtonFilters;
  }, [scrollToButtonFilters]);
  const filtersRef = useRef();

  useEffect(() => {
    filtersRef.current = children;
  }, [children]);

  useEffect(() => {
    if (scrollToButtonFiltersRef.current && !noAutoScroll) {
      scrollToButtonFiltersRef.current();
    }

    if (currentFilter?.id && filtersRef.current) {
      const filterIndex = filtersRef.current?.findIndex((f) => f?.props?.id === currentFilter.id);
      setFilterSelectedElement(filtersElementsRef?.current?.[filterIndex + 1]);
    }
  }, [currentFilter, filtersElementsRef, noAutoScroll]);

  useEffect(() => {
    return () => {
      if (scrollTimer.current) {
        clearTimeout(scrollTimer.current);
      }
    };
  }, []);

  const measuredWrapperSize = useCallback(
    (node) => {
      if (node) {
        const { top, height, width } = node.getBoundingClientRect();
        const scrollY = scrollElement.scrollTop ?? scrollElement.scrollY;

        initialWrapperPosition.current = top - scrollY - navbarHeight;
        setWrapperSize({ width, height });
      }
    },
    [scrollElement, navbarHeight],
  );

  return (
    <Wrapper
      ref={measuredWrapperSize}
      className={className}
      fixedOffset={fixedContentSize}
      contentOffset={spaceAboveFilters}
      hasAutoStickyBehaviour={hasAutoStickyBehaviour}
      $hasBackButton={onBackClick}
    >
      {onBackClick ? (
        <BackButton onClick={onBackClick}>
          <Cross />
        </BackButton>
      ) : null}
      <ScrollViewWrapper>
        <BScrollView
          vertical={false}
          shouldScrollToElement={!!filterSelectedElement}
          elementToScrollTo={filterSelectedElement}
          // We want to make the next element always visible
          // Se we add a negative offset
          offsetX={-wrapperSize.width + WRAP_SPACING + filterSelectedElement?.offsetWidth || 0}
          isOverflowing
        >
          <InnerWrap>
            <List className={FilterWrapperListClassName}>{children}</List>
          </InnerWrap>
        </BScrollView>
      </ScrollViewWrapper>
    </Wrapper>
  );
};

export default React.memo(FiltersWrapper);
